import React from "react";

const ExpandedProgressBar = ({ progress, unitNumber, onClose }) => {
  const milestones = [20, 40, 60, 80, 100];

  // Define the unit themes and chapters based on what's already in the app
  const unitStories = {
    1: {
      theme: "Growing Up",
      chapters: {
        20: "First Steps",
        40: "Childhood Years",
        60: "Academy Preparation",
        80: "Knight Academy Entrance",
        100: "Graduation",
      },
      descriptions: {
        20: "Baby Links is taking first steps into the world of addition.",
        40: "Young Links is learning to count and add bigger numbers.",
        60: "Links is preparing for the academy by mastering addition skills.",
        80: "Links enters the academy with confidence in addition.",
        100: "Links has fully mastered addition!",
      },
      emojis: {
        20: "👶",
        40: "🧒",
        60: "👦",
        80: "🏫",
        100: "🎓",
      },
    },
    2: {
      theme: "Knight Training",
      chapters: {
        20: "Shield Training",
        40: "Sword Mastery",
        60: "Horseback Riding",
        80: "Knight Graduation",
        100: "Royal Knight",
      },
      descriptions: {
        20: "Links is learning to defend with subtraction basics.",
        40: "Links is becoming skilled with subtraction techniques.",
        60: "Links rides through complex subtraction problems.",
        80: "Links has graduated from the subtraction academy.",
        100: "Links has become a royal knight of subtraction!",
      },
      emojis: {
        20: "🛡️",
        40: "⚔️",
        60: "🐎",
        80: "🎓",
        100: "👑",
      },
    },
    3: {
      theme: "The Quest",
      chapters: {
        20: "The Journey Begins",
        40: "Through the Forest",
        60: "Mountain Crossing",
        80: "Meeting the Gold Master",
        100: "Master Multiplier",
      },
      descriptions: {
        20: "Links begins the journey into multiplication.",
        40: "Links explores deeper into multiplication concepts.",
        60: "Links climbs the steep challenges of complex multiplication.",
        80: "Links meets the wise master of multiplication.",
        100: "Links has become a master of multiplication!",
      },
      emojis: {
        20: "🗺️",
        40: "🌲",
        60: "⛰️",
        80: "🧙‍♂️",
        100: "✨",
      },
    },
    4: {
      theme: "To The Castle",
      chapters: {
        20: "The Great River",
        40: "Trial of Fire",
        60: "The Dragon's Lair",
        80: "Castle Approach",
        100: "Division Champion",
      },
      descriptions: {
        20: "Links begins crossing the river of division basics.",
        40: "Links faces the fiery challenges of division.",
        60: "Links encounters the division dragon's complex problems.",
        80: "Links approaches the castle of division mastery.",
        100: "Links has conquered the castle and mastered division!",
      },
      emojis: {
        20: "🌊",
        40: "🔥",
        60: "🐉",
        80: "🏰",
        100: "👑",
      },
    },
  };

  const unitBackgrounds = {
    1: "#9CDAED", // Light blue for Addition
    2: "#FFD166", // Yellow for Subtraction
    3: "#06D6A0", // Green for Multiplication
    4: "#EF476F", // Pink for Division
  };

  // Get the proper unit data
  const unitData = unitStories[unitNumber] || unitStories[1];
  const unitColor = unitBackgrounds[unitNumber] || "#9CDAED";

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        animationName: "fadeInBackground",
        animationDuration: "0.3s",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#fff9e9",
          borderRadius: "16px",
          padding: "30px",
          width: "90%",
          maxWidth: "600px",
          boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
          animation: "expandProgressBar 0.4s ease-out",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            backgroundColor: "transparent",
            border: "none",
            fontSize: "24px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          ✕
        </button>

        {/* Title */}
        <h2
          style={{
            textAlign: "center",
            marginTop: "10px",
            marginBottom: "30px",
            color: "#333",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          Unit {unitNumber}: {unitData.theme}
        </h2>

        {/* Progress Bar */}
        <div
          style={{
            position: "relative",
            height: "20px",
            backgroundColor: "#e0e0e0",
            borderRadius: "10px",
            marginBottom: "80px",
          }}
        >
          <div
            style={{
              position: "absolute",
              height: "100%",
              width: `${progress}%`,
              backgroundColor: unitColor,
              borderRadius: "10px",
              transition: "width 1s ease",
            }}
          />

          {/* Milestone markers */}
          {milestones.map((milestone) => (
            <div
              key={milestone}
              style={{
                position: "absolute",
                left: `${milestone}%`,
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: milestone === 100 ? "10px" : "16px",
                height: milestone === 100 ? "10px" : "16px",
                borderRadius: "50%",
                backgroundColor: progress >= milestone ? "#fff" : "#aaa",
                border: `3px solid ${
                  progress >= milestone ? unitColor : "#ddd"
                }`,
                zIndex: 2,
              }}
            />
          ))}

          {/* Progress percentage */}
          <div
            style={{
              position: "absolute",
              left: `${Math.min(Math.max(progress, 5), 95)}%`,
              top: "-25px",
              transform: "translateX(-50%)",
              fontSize: "16px",
              fontWeight: "bold",
              color: "#333",
            }}
          >
            {progress}%
          </div>
        </div>

        {/* Milestones with descriptions */}
        <div style={{ display: "flex", flexDirection: "column", gap: "25px" }}>
          {milestones.map((milestone) => {
            const isCompleted = progress >= milestone;
            const isCurrent =
              progress < milestone && progress >= milestone - 20;

            return (
              <div
                key={milestone}
                style={{
                  padding: "15px",
                  borderRadius: "10px",
                  backgroundColor: isCompleted
                    ? `${unitColor}22`
                    : "transparent",
                  border: isCurrent
                    ? `2px dashed ${unitColor}`
                    : "2px solid transparent",
                  opacity: isCompleted || isCurrent ? 1 : 0.6,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "15px",
                }}
              >
                <div
                  style={{
                    fontSize: "30px",
                    marginTop: "-5px",
                  }}
                >
                  {unitData.emojis[milestone] || "🔮"}
                </div>

                <div
                  style={{
                    flex: 1, // Make this div take available space for text alignment
                    textAlign: "center", // Center the text content
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 5px 0",
                      fontSize: "18px",
                      color: "#333",
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    {unitData.chapters[milestone] || `Milestone ${milestone}%`}
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#555",
                      fontFamily: "'Inter', sans-serif",
                      textAlign: "center", // Ensure paragraph text is centered
                    }}
                  >
                    {unitData.descriptions[milestone] ||
                      "Continue learning to reach this milestone!"}
                  </p>
                </div>

                <div
                  style={{
                    marginLeft: "auto",
                    fontWeight: "bold",
                    color: isCompleted ? "#4CAF50" : "#999",
                  }}
                >
                  {isCompleted ? "✓" : milestone + "%"}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>
        {`
          @keyframes fadeInBackground {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes expandProgressBar {
            from { 
              opacity: 0;
              transform: translateY(20px) scale(0.95);
            }
            to { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>
    </div>
  );
};

export default ExpandedProgressBar;
